import React, { useEffect, useState } from 'react';
import styled from '@emotion/styled';

import { detectOS } from '../utilities/detectOS';
import { isBrowser, removeNoTranslateWrapper } from '../utilities/misc';
import { colors } from '../theme/colors';
import { rem } from '../utilities/style';
import { shadow } from '../styles/box';
import { headers } from '../styles/type';
import { fontWeights } from '../theme/type';
import { media } from '../theme/media';

import TextRenderer from './TextRenderer';
import Link from './Link';
import DownloadOption from './DownloadOption';

const Container = styled.div`
  ${shadow}
  display: flex;
  flex-direction: column;
  padding: ${rem(20)};
  background-color: ${colors.white};
  border-radius: ${rem(10)};
`;

const Headline = styled.h3`
  ${headers.h4}
`;

const Text = styled.p`
  margin-top: ${rem(4)};
  color: ${colors.gray['600']};

  strong {
    font-weight: ${fontWeights.semiBold};
    color: ${colors.blue['800']};
  }
`;

const TextSmall = styled.p`
  margin-top: ${rem(8)};
  font-size: ${rem(14)};
  color: ${colors.gray['600']};
`;

const Items = styled.ul`
  display: grid;
  gap: ${rem(8)};
  margin-top: ${rem(24)};

  ${({ columns }) => columns === 2 && `
    ${media('>tiny')} {
      grid-template-columns: 1fr 1fr;
    }
  `}
`;

const Item = styled.li`
`;

const SecondaryList = styled.ul`
  margin-top: auto;
  padding-top: ${rem(48)};
`;

const SecondaryLink = styled(Link)`
  margin-top: ${rem(16)};
  font-weight: ${fontWeights.medium};
  color: ${colors.blue['800']};
  text-decoration: underline;

  &:focus-visible,
  &:hover {
    text-decoration: none;
  }
`;

const DownloadCard = ({
  header,
  columns,
  items,
  itemsSecondary,
  itemDetails,
}) => {
  const [currentOs, setCurrentOs] = useState(null);

  useEffect(() => {
    if (isBrowser) {
      const { userAgent } = window.navigator;
      setCurrentOs(detectOS(userAgent));
    }
  }, []);

  return (
    <Container>
      {header && (
        <>
          {header.headline && (
            <Headline>
              <TextRenderer text={header.headline} />
            </Headline>
          )}
          {header.description && (
            <Text dangerouslySetInnerHTML={{ __html: header.description }} />
          )}
        </>
      )}

      {items && (
        <Items columns={columns}>
          {items.map(({
            os,
            buttonText,
            buttonLink,
            dropdownText,
            dropdownItems,
          }, index) => (
            // eslint-disable-next-line react/no-array-index-key
            <Item key={index}>
              <DownloadOption
                icon={removeNoTranslateWrapper(os)}
                buttonText={buttonText}
                buttonLink={buttonLink}
                dropdownText={dropdownText && dropdownText}
                dropdownItems={dropdownItems && dropdownItems}
                active={currentOs && currentOs.includes(removeNoTranslateWrapper(os))}
              />
            </Item>
          ))}
        </Items>
      )}

      {itemsSecondary && (
        <SecondaryList>
          {itemsSecondary.map(({
            buttonText,
            buttonLink,
          }, index) => (
            // eslint-disable-next-line react/no-array-index-key
            <Item key={index}>
              <SecondaryLink to={buttonLink}>
                {buttonText}
              </SecondaryLink>
            </Item>
          ))}
        </SecondaryList>
      )}

      {itemDetails && itemDetails.map((text, index) => (
        // eslint-disable-next-line react/no-array-index-key
        <TextSmall key={index}>
          <TextRenderer text={text} />
        </TextSmall>
      ))}
    </Container>
  );
};

export default DownloadCard;
